import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QUIZ_QUERY_KEYS } from './quizzes';
import { useImproveAxios } from '../../hooks/useImproveAxios';
import { customInstance } from '../axios-instance';
import {
	getImproveAPI,
	type CreateQuestionBody,
	type CreateQuestionResponse,
	type QuestionSlimDto,
	type QuizWithQuestionsDto,
	type SearchQuestionsBody,
	type SearchQuestionsResponse,
} from '../gen';

/** Re-export Orval-generated DTOs (`improve-api/client`: `npm run swagger` then `npm run generate:local`). */
export type CreateQuizQuestionBody = CreateQuestionBody;
export type CreateQuizQuestionResponse = CreateQuestionResponse & {
	hint?: string | null;
	answer?: boolean | null;
	options?: string[] | null;
	correctAnswer?: string | null;
	correctAnswers?: string[] | null;
};
export interface UpdateQuizQuestionBody {
	title: string;
	hint?: string;
	answer?: boolean;
	options?: string[];
	correctAnswer?: string;
	correctAnswers?: string[];
}

export interface UpdateQuizQuestionResponse {
	id: number;
	__type: string;
	title: string;
	hint?: string | null;
	answer?: boolean | null;
	options?: string[] | null;
	correctAnswer?: string | null;
	correctAnswers?: string[] | null;
}

/**
 * Quiz builder types - using generated types from improve-api.
 * The optional fields are present when admin edit needs the full question body.
 */
export type QuestionSlim = QuestionSlimDto & {
	hint?: string | null;
	answer?: boolean | null;
	options?: string[] | null;
	correctAnswer?: string | null;
	correctAnswers?: string[] | null;
};
export type QuizWithQuestions = Omit<QuizWithQuestionsDto, "composition" | "questions"> & {
	composition?: QuestionSlim[];
	questions: Record<string, QuestionSlim[]>;
};
export type SearchQuestionsParams = SearchQuestionsBody;
export type SearchQuestionsResult = SearchQuestionsResponse;

/**
 * Hook to fetch quiz with full question composition for admin builder
 */
export function useGetQuizWithQuestions(quizId: number | undefined) {
	const axiosConfig = useImproveAxios();
	const { quizzesControllerGetWithQuestions } = getImproveAPI();

	return useQuery<QuizWithQuestions>({
		queryKey: [QUIZ_QUERY_KEYS.BUILDER, quizId],
		queryFn: async () => {
			if (!quizId) throw new Error('Quiz ID is required');
			const response = await quizzesControllerGetWithQuestions(quizId, axiosConfig);
			return response.data as QuizWithQuestions;
		},
		enabled: !!quizId,
		staleTime: 30 * 1000,
	});
}

/**
 * Hook to search questions for quiz builder
 */
export function useSearchQuestions() {
	const axiosConfig = useImproveAxios();
	const { quizzesControllerSearchQuestions } = getImproveAPI();

	return useMutation<SearchQuestionsResponse, Error, SearchQuestionsParams>({
		mutationFn: async (params: SearchQuestionsParams) => {
			const response = await quizzesControllerSearchQuestions(params, axiosConfig);
			return response.data;
		},
	});
}

export function useCreateQuizQuestion() {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { quizzesControllerCreateQuestion } = getImproveAPI();

	return useMutation<CreateQuizQuestionResponse, Error, CreateQuestionBody>({
		mutationFn: async (body) => {
			const response = await quizzesControllerCreateQuestion(body, axiosConfig);
			return response.data as CreateQuizQuestionResponse;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		},
	});
}

export function useUpdateQuizQuestion() {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();

	return useMutation<
		UpdateQuizQuestionResponse,
		Error,
		{ questionType: string; questionId: number; body: UpdateQuizQuestionBody }
	>({
		mutationFn: async ({ questionType, questionId, body }) => {
			const response = await customInstance<UpdateQuizQuestionResponse>({
				url: `/api/_internal/quizzes/questions/${questionType}/${questionId}`,
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				data: body,
			}, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.BUILDER] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		},
	});
}

/**
 * Hook to update quiz composition
 * Uses the existing quizzesControllerUpdate endpoint with composition field.
 */
export function useUpdateQuizComposition(quizId: number) {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { quizzesControllerUpdate } = getImproveAPI();

	return useMutation<void, Error, QuestionSlimDto[]>({
		mutationFn: async (composition: QuestionSlimDto[]) => {
			await quizzesControllerUpdate(quizId, { composition } as any, axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.DETAIL, quizId] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.BUILDER, quizId] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		},
	});
}
